/**
 * Attribute definition schemas.
 * Defines schemas for attribute metadata (not values).
 * Re-exports and enhances schemas from attribute.ts for modular use.
 */
import { z } from "zod";
// ============================================================================
// Re-export core schemas from attribute.ts
// ============================================================================
export { AttributeTypeSchema, ComparisonOperatorSchema, AggregationOperatorSchema, StringConstraintsSchema, NumberConstraintsSchema, EnumOptionSchema, EnumConstraintsSchema, DateConstraintsSchema, CurrencyConstraintsSchema, PercentageConstraintsSchema, UrlConstraintsSchema, ArrayConstraintsSchema, EmptyConstraintsSchema, AttributeConstraintsSchema, AttributeDisplaySchema, AttributeDefinitionSchema, DerivedAttributeDefinitionSchema, } from "./attribute";
// ============================================================================
// Attribute Comparison Configuration
// ============================================================================
/** Normalization method for ranking */
export const NormalizationMethodSchema = z.enum([
    "min-max", // Scale to 0-1 range
    "z-score", // Standard deviation normalization
    "log", // Logarithmic scaling for large ranges
    "percentile", // Percentile ranking
    "none", // No normalization
]);
/** Attribute comparison configuration for ranking engine */
export const AttributeComparisonSchema = z
    .object({
    comparable: z.boolean().default(false),
    higherIsBetter: z.boolean().default(true),
    normalize: NormalizationMethodSchema.default("none"),
    weight: z.number().min(0).max(1).default(1),
    missingValueStrategy: z.enum(["exclude", "min", "max", "average"]).default("exclude"),
})
    .strict();
// ============================================================================
// Extended Attribute Definition (with comparison config)
// ============================================================================
/** Extended attribute definition including comparison configuration */
export const ExtendedAttributeDefinitionSchema = z
    .object({
    key: z
        .string()
        .min(1)
        .regex(/^[a-z][a-zA-Z0-9]*$/, "Key must be camelCase"),
    type: z.enum([
        "string",
        "number",
        "boolean",
        "enum",
        "date",
        "currency",
        "percentage",
        "url",
        "email",
        "phone",
        "array",
    ]),
    label: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    required: z.boolean(),
    constraints: z.unknown().optional(), // Validated by type-specific schemas
    display: z
        .object({
        format: z.string().optional(),
        prefix: z.string().optional(),
        suffix: z.string().optional(),
        showInCard: z.boolean().optional(),
        showInDetails: z.boolean().optional(),
        showInComparison: z.boolean().optional(),
        priority: z.number().int().min(0).optional(),
        group: z.string().optional(),
        icon: z.string().optional(),
    })
        .strict()
        .optional(),
    comparison: AttributeComparisonSchema.optional(),
    defaultValue: z.unknown().optional(),
    computed: z.boolean().optional(),
    computeExpression: z.string().optional(),
})
    .strict()
    .refine((data) => {
    // If computed is true, computeExpression must be provided
    if (data.computed === true && !data.computeExpression) {
        return false;
    }
    return true;
}, {
    message: "computeExpression is required when computed is true",
    path: ["computeExpression"],
});
// ============================================================================
// Attribute Definition Helpers
// ============================================================================
/** Check if an attribute definition represents a numeric type */
export function isNumericAttribute(definition) {
    return ["number", "currency", "percentage"].includes(definition.type);
}
/** Check if an attribute definition represents a string-like type */
export function isStringAttribute(definition) {
    return ["string", "url", "email", "phone"].includes(definition.type);
}
/** Check if an attribute definition represents a collection type */
export function isCollectionAttribute(definition) {
    return definition.type === "array";
}
/** Check if an attribute definition supports range comparisons */
export function supportsRangeComparison(definition) {
    return ["number", "currency", "percentage", "date"].includes(definition.type);
}
/** Get the supported operators for an attribute type */
export function getSupportedOperators(type) {
    switch (type) {
        case "string":
        case "url":
        case "email":
        case "phone":
            return [
                "eq",
                "neq",
                "contains",
                "not_contains",
                "starts_with",
                "ends_with",
                "in",
                "not_in",
                "regex",
                "is_null",
                "is_not_null",
            ];
        case "number":
        case "currency":
        case "percentage":
            return [
                "eq",
                "neq",
                "gt",
                "gte",
                "lt",
                "lte",
                "between",
                "in",
                "not_in",
                "is_null",
                "is_not_null",
            ];
        case "boolean":
            return ["eq", "neq", "is_null", "is_not_null"];
        case "enum":
            return ["eq", "neq", "in", "not_in", "is_null", "is_not_null"];
        case "date":
            return ["eq", "neq", "gt", "gte", "lt", "lte", "between", "is_null", "is_not_null"];
        case "array":
            return ["contains", "not_contains", "is_null", "is_not_null", "exists"];
        default:
            return ["eq", "neq", "is_null", "is_not_null"];
    }
}
/** Validate that an operator is valid for an attribute type */
export function isValidOperatorForType(type, operator) {
    const supportedOperators = getSupportedOperators(type);
    return supportedOperators.includes(operator);
}
